import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateDirective } from '@ngx-translate/core';
import { RouterBaseService } from '@tdwb/core';
import { ButtonComponent } from '../../../components/button/button.component';
import { DividerComponent } from '../../../components/panel/divider.component';
import { LoadingIndicatorComponent } from '../../../components/loading-indicator/loading-indicator.component';
import { NotificationComponent } from '../../../components/notification/notification.component';
import { HrefDirective } from '../../../directives/href/href.directive';
import { W8BenFormErrorComponent } from '../w8ben-form-error/w8ben-form-error.component';

import { W8BenFormService } from '../w8ben-form.service';
import { W8BenModalName, W8BenProfile } from '../w8ben-form.domain';
import { W8BenFormAnalyticsService } from '../w8ben-form-analytics.service';

@Component({
  selector: 'td-wb-w8ben-preview',
  templateUrl: 'w8ben-form-preview.component.html',
  styleUrls: ['w8ben-form-preview.component.scss'],
  imports: [
    DividerComponent,
    ButtonComponent,
    TranslateDirective,
    LoadingIndicatorComponent,
    W8BenFormErrorComponent,
    NotificationComponent,
    HrefDirective
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class W8BenFormPreviewComponent implements OnInit {
  @Input() retainData: boolean = false;
  protected profile: W8BenProfile | undefined;
  protected loading: boolean = false;
  private destroyRef: DestroyRef = inject(DestroyRef);

  constructor(private routerService: RouterBaseService,
              private w8benFormService: W8BenFormService,
              private changeDetectionRef: ChangeDetectorRef,
              private w8BenFormAnalyticsService: W8BenFormAnalyticsService) {
  }

  ngOnInit(): void {
    this.w8BenFormAnalyticsService.setTaggingData(W8BenModalName.UPDATE, 'n/a');
    if (this.w8benFormService.profile && this.retainData) {
      this.profile = this.w8benFormService.profile;
    } else {
      this.loading = true;
      this.w8benFormService.getProfile()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (profile: W8BenProfile) => {
            this.profile = profile;
            this.loading = false;
            this.changeDetectionRef.markForCheck();
          },
          error: (): void => {
            this.loading = false;
            this.changeDetectionRef.markForCheck();
          },
          complete(): void {
          }
        });
    }
  }

  protected getModalId(): string {
    return this.w8BenFormAnalyticsService.getModalId();
  }

  protected showSin(): boolean {
    return this.w8benFormService.isTaxResidencyCanada() ||
           (this.getSin() !== '' && !this.profile?.ftinNotRequired);
  }

  protected onClickUpdate(): void {
    this.routerService.go('modal.w8ben.update');
  }

  protected onClickConfirm(): void {
    this.routerService.go('modal.w8ben.tax_residency_benefits');
  }

  protected getSin(): string {
    return this.w8benFormService.getIdentification();
  }

  protected requiredFieldMissing(): boolean {
    return !this.profile?.citizenship ||
           !this.profile?.birthDt ||
           (this.profile?.legalName ?? '').trim() === '';
  }

  protected addressMissing(): boolean {
    return !this.profile?.residenceAddressLines?.some((line: string) => line.trim() !== '');
  }
}

